import * as React from "react";
import { supabase } from "@/integrations/supabase/client";
import { ensureDemoAccounts } from "@/lib/demo-setup.functions";


export type Role = "admin" | "teacher" | "student";

export type AuthUser = {
  id: string;
  email: string;
  displayName: string;
  avatarKey: string | null;
  avatarUrl: string | null;
  role: Role;
  onboarded: boolean;
  centerId: string | null;
};

type DemoUser = { name: string; email: string; role: Role };

type AuthCtx = {
  user: AuthUser | null;
  loading: boolean;
  isDemo: boolean;
  demoRole: Role | null;
  enterDemo: (role: Role) => void;
  exitDemo: () => void;
  demoSignIn: (role: Role) => Promise<void>;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  /** @deprecated giữ tương thích với code cũ — dùng demoSignIn. */
  quickLogin: (role: Role) => DemoUser;
};


const DEMO_KEY = "ezyclass.demo.role";
const LEGACY_DEMO_KEY = "eduflow.demo.role";
const DEMO_PROFILES: Record<Role, { name: string; email: string }> = {
  admin: { name: "Quản trị viên Demo", email: "admin@demo.ezyclass" },
  teacher: { name: "Giáo viên Demo", email: "teacher@demo.ezyclass" },
  student: { name: "Học sinh Demo", email: "student@demo.ezyclass" },
};

const Ctx = React.createContext<AuthCtx | null>(null);

export const ROLE_HOME: Record<Role, string> = {
  admin: "/admin",
  teacher: "/teacher",
  student: "/student",
};

function readDemoRole(): Role | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(DEMO_KEY) ?? window.localStorage.getItem(LEGACY_DEMO_KEY);
  return v === "admin" || v === "teacher" || v === "student" ? v : null;
}

async function loadUserFromSupabase(userId: string, fallbackEmail?: string): Promise<AuthUser | null> {
  const [profileRes, rolesRes] = await Promise.all([
    supabase
      .from("profiles")
      .select("id, email, display_name, avatar_key, avatar_url, onboarded, center_id, extra_info")
      .eq("id", userId)
      .maybeSingle(),
    supabase.from("user_roles").select("role").eq("user_id", userId),
  ]);
  // Profile/role có thể chưa kịp tạo (race với ensureUserRole). KHÔNG trả null
  // — tránh portal guard đẩy về /login khi session đã hợp lệ.
  const p = profileRes.data;
  const role = (rolesRes.data?.[0]?.role ?? null) as Role | null;
  const email = p?.email ?? fallbackEmail ?? "";
  return {
    id: userId,
    email,
    displayName: p?.display_name ?? (email ? email.split("@")[0] : "Người dùng"),
    avatarKey: p?.avatar_key ?? null,
    avatarUrl: p?.avatar_url ?? null,
    role: role ?? "student",
    onboarded: p?.onboarded ?? false,
    centerId: (p as { center_id?: string | null } | null)?.center_id ?? null,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [demoRole, setDemoRole] = React.useState<Role | null>(null);

  const refresh = React.useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const sessionUser = data.session?.user;
    if (!sessionUser) {
      setUser(null);
      return;
    }
    const u = await loadUserFromSupabase(sessionUser.id, sessionUser.email ?? undefined);
    setUser(u);
  }, []);

  React.useEffect(() => {
    setDemoRole(readDemoRole());

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        setUser(null);
        setLoading(false);
        return;
      }
      setTimeout(() => {
        loadUserFromSupabase(session.user.id, session.user.email ?? undefined).then((u) => {
          setUser(u);
          setLoading(false);
        });
      }, 0);
    });

    refresh().finally(() => setLoading(false));

    const onStorage = (e: StorageEvent) => {
      if (e.key === DEMO_KEY) setDemoRole(readDemoRole());
    };
    window.addEventListener("storage", onStorage);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh]);

  const enterDemo = React.useCallback((role: Role) => {
    if (typeof window !== "undefined") window.localStorage.setItem(DEMO_KEY, role);
    setDemoRole(role);
  }, []);

  const exitDemo = React.useCallback(() => {
    if (typeof window !== "undefined") window.localStorage.removeItem(DEMO_KEY);
    setDemoRole(null);
  }, []);

  const logout = React.useCallback(async () => {
    setUser(null);
    exitDemo();
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("ezyclass.saved_email");
      window.localStorage.removeItem("ezyclass.saved_role");
      window.localStorage.removeItem("ezyclass.remember");
    }
    await supabase.auth.signOut();
  }, [exitDemo]);

  const demoSignIn = React.useCallback(async (role: Role) => {
    const email = DEMO_PROFILES[role].email;
    enterDemo(role); // bật flag demo trước để banner + read-only kích hoạt ngay
    const trySignIn = () =>
      supabase.auth.signInWithPassword({ email, password: "demo1234" });
    let { error } = await trySignIn();
    if (error) {
      // Account có thể chưa tồn tại — gọi ensureDemoAccounts rồi retry
      try {
        await ensureDemoAccounts();
        ({ error } = await trySignIn());
      } catch (e) {
        console.error("[demo] ensureDemoAccounts failed:", e);
      }
    }
    if (error) {
      console.error("[demo] sign-in failed:", error.message);
    }
  }, [enterDemo]);

  const value: AuthCtx = {
    user,
    loading,
    isDemo: !!demoRole,
    demoRole,
    enterDemo,
    exitDemo,
    demoSignIn,
    refresh,
    logout,
    quickLogin: (role) => {
      enterDemo(role);
      return { ...DEMO_PROFILES[role], role };
    },
  };


  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Vai trò hiện tại — ưu tiên user thật, sau đó demo. */
export function useActiveRole(): Role | null {
  const { user, demoRole } = useAuth();
  return user?.role ?? demoRole ?? null;
}

/** Demo chỉ xem, không thao tác được. */
export function useCanMutate(): boolean {
  const { isDemo } = useAuth();
  return !isDemo;
}

/** Hiển thị tên cho UI. */
export function useDisplayInfo() {
  const { user, demoRole } = useAuth();
  if (user) return { name: user.displayName, email: user.email, avatarKey: user.avatarKey, avatarUrl: user.avatarUrl };
  if (demoRole) {
    const p = DEMO_PROFILES[demoRole];
    return { name: p.name, email: p.email, avatarKey: null as string | null, avatarUrl: null as string | null };
  }
  return { name: "", email: "", avatarKey: null as string | null, avatarUrl: null as string | null };
}
